import { jwtDecode } from "jwt-decode";
import {
  importRsaPublicKey,
  generateAesKey,
  encryptPayloadWithAes,
  encryptAesKeyWithRsa,
  decryptResponseWithAes,
} from "../utils/cryptoUtils";
import { API_BASE } from "./BaseUrl";
import { refreshKeyProactive } from "../utils/secureApi";
import { hasUnsavedChanges } from "../utils/unsavedGuard";

/* ---------------- STATE ---------------- */
let logoutListenerActive = false;
let activityListenerActive = false;
let tokenTimerInterval = null;

// Gate for in-flight token-refresh races: once a logout starts we must
// ignore any /users/refresh-token response that arrives *after* the
// clearToken() call, otherwise the stale token gets re-written.
let loggedOut = false;

// Shared refresh state - exported so BaseUrl.js can check it
export let refreshInProgress = false;
let lastRefreshAttempt = 0;

// Activity tracking
let lastActivity = Date.now();
// eslint-disable-next-line no-unused-vars
let lastActivityType = "init";

// Proactive RSA key refresh cadence — piggy-backs on the 1-second token
// timer. Kept comfortably under CryptoKeyManager's 30-min server rotation
// and secureApi.js's 25-min CACHE_TTL so an active tab never sits on a
// stale key. Reset in startTokenTimer() to align with post-login state.
let lastKeyRefreshAt = Date.now();
const KEY_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

/* ---------------- TOKEN HELPERS ---------------- */
export const getToken = () => localStorage.getItem("token");
export const setToken = (token) => {
  if (loggedOut) return; // refuse to resurrect a logged-out session
  localStorage.setItem("token", token);
};

const getRefreshToken = () => localStorage.getItem("refreshToken");
export const setRefreshToken = (refreshToken) => {
  if (loggedOut) return;
  localStorage.setItem("refreshToken", refreshToken);
};

// Keys belonging to the shared-data caches (MasterDataContext, CategoryCacheContext).
// Kept here so logout wipes every bit of the previous user's footprint.
const CACHE_KEYS = [
  "md_companies", "md_stores", "md_vendors", "md_customers",
  "md_users", "md_products", "md_roles",
  "categoryCache",
  "userPermissions",
  "companyPermissions",
  "companyPricing",
  "lastActivity",
];

export const clearToken = () => {
  loggedOut = true;
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("loggedUser");
  CACHE_KEYS.forEach((k) => localStorage.removeItem(k));
  // Reset the module-level boot cache so the next login fetches fresh data.
  import("./bootService").then((m) => m.resetBootCache()).catch(() => {});
};

// Called by the login flow once a fresh session starts, so subsequent
// setToken() / setRefreshToken() writes are no longer blocked.
export const markLoggedIn = () => { loggedOut = false; };

const decodeToken = () => {
  try {
    const token = getToken();
    return token ? jwtDecode(token) : null;
  } catch {
    return null;
  }
};

export const isTokenExpired = () => {
  const decoded = decodeToken();
  if (!decoded?.exp) return true;
  return decoded.exp < Date.now() / 1000;
};

/* ---------------- DYNAMIC TIME CALCULATION ---------------- */
// eslint-disable-next-line no-unused-vars
const getTokenTimings = () => {
  const decoded = decodeToken();
  if (!decoded?.exp || !decoded?.iat) return null;

  const totalLifetimeSec = decoded.exp - decoded.iat;

  return {
    totalLifetimeMs: totalLifetimeSec * 1000,
    idleTimeoutMs: totalLifetimeSec * 0.3 * 1000, // 30%
    refreshBeforeMs: totalLifetimeSec * 0.1 * 1000, // 10%
    expiresAtMs: decoded.exp * 1000,
  };
};

// eslint-disable-next-line no-unused-vars
const getRemainingMs = () => {
  const decoded = decodeToken();
  if (!decoded?.exp) return 0;
  return decoded.exp * 1000 - Date.now();
};

const restartTokenTimer = () => {
  // Token timer restarted
  stopTokenTimer();
  startTokenTimer();
};

/* ---------------- ACTIVITY TRACKING ---------------- */
// Track if user just became active (for refresh trigger)
let userJustBecameActive = false;
let wasIdle = true; // Start as idle
const IDLE_TIMEOUT_MS = 30_000; // 30 seconds idle (refresh-window gate)
const IDLE_LOGOUT_MS = 30 * 60 * 1000; // 30 minutes idle → auto-logout
// Throttle cross-tab activity broadcasts so localStorage isn't churned on every mousemove.
const ACTIVITY_BROADCAST_THROTTLE_MS = 5_000;
let lastActivityBroadcast = 0;

export const updateActivity = async (eventType = "unknown") => {
  const now = Date.now();
  const wasIdleBefore = wasIdle;
  const timeSinceLastActivity = now - lastActivity;

  lastActivity = now;
  lastActivityType = eventType;
  wasIdle = false;

  // Broadcast activity to other tabs so a tab the user is actively using
  // keeps idle tabs from auto-logging-out the whole session.
  if (!loggedOut && now - lastActivityBroadcast >= ACTIVITY_BROADCAST_THROTTLE_MS) {
    lastActivityBroadcast = now;
    try { localStorage.setItem("lastActivity", String(now)); } catch {}
  }

  // If user was idle and now became active (clicked/touched), refresh token immediately
  if (wasIdleBefore && timeSinceLastActivity > IDLE_TIMEOUT_MS) {
    userJustBecameActive = true;

    // Immediately refresh token when user comes back from idle
    const token = getToken();
    if (token && !isTokenExpired()) {
      const refreshed = await refreshToken("user-activity");
      if (refreshed) {
        // Token refreshed on user activity
      }
    }
  }
};

const isUserIdle = () => {
  // idle = no activity for last 30 seconds
  const idle = Date.now() - lastActivity > IDLE_TIMEOUT_MS;
  if (idle && !wasIdle) {
    wasIdle = true;
  }
  return idle;
};

// Check if user just became active and reset the flag
export const checkAndResetActivityFlag = () => {
  const wasActive = userJustBecameActive;
  userJustBecameActive = false;
  return wasActive;
};

export const setupActivityTracking = () => {
  if (activityListenerActive) return;
  activityListenerActive = true;

  const handler = (e) => updateActivity(e.type);

  // Track click, touch, keypress, scroll events
  document.addEventListener("click", handler, true);
  document.addEventListener("touchstart", handler, true);
  document.addEventListener("keypress", handler, true);
  document.addEventListener("scroll", handler, true);
  document.addEventListener("mousemove", handler, true);

  // Cross-tab activity sync — pick up the latest activity timestamp from any other tab.
  const storageHandler = (e) => {
    if (e.key === "lastActivity" && e.newValue) {
      const value = parseInt(e.newValue, 10);
      if (!Number.isNaN(value) && value > lastActivity) {
        lastActivity = value;
        wasIdle = false;
      }
    }
  };
  window.addEventListener("storage", storageHandler);

  return () => {
    document.removeEventListener("click", handler, true);
    document.removeEventListener("touchstart", handler, true);
    document.removeEventListener("keypress", handler, true);
    document.removeEventListener("scroll", handler, true);
    document.removeEventListener("mousemove", handler, true);
    window.removeEventListener("storage", storageHandler);
    activityListenerActive = false;
  };
};

/* ---------------- REFRESH TOKEN ---------------- */
export const refreshToken = async (source = "timer") => {
  // Prevent multiple simultaneous refresh attempts
  if (refreshInProgress) {
    return false;
  }

  // Prevent rapid refresh attempts (minimum 3 seconds between attempts)
  const now = Date.now();
  if (now - lastRefreshAttempt < 3000) {
    return false;
  }

  try {
    const token = getToken();
    const refreshTokenValue = getRefreshToken();

    if (!token) {
      return false;
    }

    if (!refreshTokenValue) {
      return false;
    }

    // Check if token is already expired - don't try to refresh
    const decoded = jwtDecode(token);
    if (!decoded?.exp || decoded.exp * 1000 <= Date.now()) {
      return false;
    }

    refreshInProgress = true;
    lastRefreshAttempt = now;


    // Fetch RSA key for encryption
    let keyRes;
    try {
      keyRes = await fetch(`${API_BASE}/users/key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      console.error("❌ Failed to fetch encryption key for refresh");
      refreshInProgress = false;
      return false;
    }
    const keyData = await keyRes.json();
    if (keyData.errCode !== 0 || !keyData.data) {
      console.error("❌ Key fetch failed");
      refreshInProgress = false;
      return false;
    }

    const { id: keyId, key: serverKey } = keyData.data;
    const rsaPublicKey = await importRsaPublicKey(serverKey);
    const aesKey = await generateAesKey();
    const encPayload = await encryptPayloadWithAes({ refreshToken: refreshTokenValue }, aesKey);
    const encAesKey = await encryptAesKeyWithRsa(aesKey, rsaPublicKey);

    const res = await fetch(`${API_BASE}/users/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: keyId, key: encAesKey, payload: encPayload }),
    });

    if (!res.ok) {
      console.error(`❌ Refresh API returned status: ${res.status}`);
      refreshInProgress = false;
      return false;
    }

    const resBody = await res.json();

    // Decrypt encrypted response
    let data;
    if (resBody.errCode === 0 && resBody.data?.data) {
      data = await decryptResponseWithAes(resBody.data.data, aesKey);
    } else {
      data = resBody;
    }

    if (data.errCode === 0 && data.data) {
      // If logout happened while this refresh was in flight, drop the result.
      if (loggedOut) {
        refreshInProgress = false;
        return false;
      }

      const newToken = data.data.token;
      const newRefreshToken = data.data.refreshToken;

      // Save both tokens
      setToken(newToken);
      setRefreshToken(newRefreshToken);

      // Keep the union permissions (read by Guard/Sidebar) in step with the map.
      if (data.data.permissions) {
        try {
          localStorage.setItem("userPermissions", JSON.stringify(data.data.permissions));
        } catch { /* ignore storage failure */ }
      }

      // Company-wise roles: keep the per-company permission map fresh so it
      // can't go stale. Only overwrite when the server re-delivers it.
      if (data.data.companyPermissions) {
        try {
          localStorage.setItem("companyPermissions", JSON.stringify(data.data.companyPermissions));
        } catch { /* ignore storage failure */ }
      }

      // Company-wise pricing map: same refresh rule as companyPermissions.
      if (data.data.companyPricing) {
        try {
          localStorage.setItem("companyPricing", JSON.stringify(data.data.companyPricing));
        } catch { /* ignore storage failure */ }
      }

      // Reset activity timer
      lastActivity = Date.now();
      wasIdle = false;

      // Broadcast token update to other components
      window.dispatchEvent(
        new CustomEvent("tokenRefreshed", { detail: newToken })
      );

      refreshInProgress = false;
      return true;
    }

    console.error(
      "❌ Invalid refresh response:",
      data.message || data.errMsg || data
    );
    refreshInProgress = false;
    return false;
  } catch (e) {
    console.error("❌ Refresh error:", e.message || e);
    refreshInProgress = false;
    return false;
  }
};

/* ---------------- FORCE LOGOUT ---------------- */
export const forceLogout = (reason = "Session ended") => {
  console.log("🚪 Logout:", reason);
  // Stash the reason so the login screen can surface it (Release B). Set BEFORE
  // clearToken so it isn't caught in the wipe; it is not in CACHE_KEYS anyway.
  try { localStorage.setItem("logoutReason", reason); } catch { /* ignore */ }
  stopTokenTimer();
  clearToken();
  localStorage.setItem("logout", Date.now().toString());
  window.location.replace("/login");
};

/**
 * Graceful forced-logout for the admin-revoke / signed-in-elsewhere case.
 * If the user has unsaved work open, offer a chance to stay and save first
 * (web writes still succeed during the grace window — the JWT is valid until it
 * expires). Deferring simply returns; the 30s heartbeat re-checks and re-prompts.
 * Use this ONLY for revocation detected by the heartbeat — NOT for token-expiry
 * or idle logout (there the session is genuinely dead / the user is away).
 * @returns true if the user was signed out, false if they chose to stay.
 */
export const gracefulForceLogout = (reason = "Session ended") => {
  if (hasUnsavedChanges()) {
    const leave = window.confirm(
      "An administrator ended your session, or you signed in on another device.\n\n" +
      "You have unsaved changes. Click OK to sign out now (your changes will be lost), " +
      "or Cancel to stay and save your work first."
    );
    if (!leave) return false; // defer — the next heartbeat will prompt again
  }
  forceLogout(reason);
  return true;
};

/* ---------------- CROSS TAB LOGOUT ---------------- */
export const setupCrossTabLogout = () => {
  if (logoutListenerActive) return;
  logoutListenerActive = true;

  const handler = (e) => {
    if (e.key === "logout" || (e.key === "token" && !e.newValue)) {
      forceLogout("Logged out from another tab");
    }
  };

  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
};

/* ---------------- TOKEN REFRESH EVENT LISTENER ---------------- */
let tokenRefreshListener = null;

const setupTokenRefreshListener = () => {
  if (tokenRefreshListener) return;

  tokenRefreshListener = (event) => {
    restartTokenTimer();
  };

  window.addEventListener("tokenRefreshed", tokenRefreshListener);
  // Also listen for storage changes from axios interceptor
  window.addEventListener("storage", (e) => {
    if (e.key === "token" && e.newValue && e.newValue !== e.oldValue) {
      restartTokenTimer();
    }
  });
};

const cleanupTokenRefreshListener = () => {
  if (tokenRefreshListener) {
    window.removeEventListener("tokenRefreshed", tokenRefreshListener);
    tokenRefreshListener = null;
  }
};

/* ---------------- MAIN TIMER ---------------- */
export const startTokenTimer = () => {
  if (tokenTimerInterval) clearInterval(tokenTimerInterval);
  // Align the proactive-refresh baseline with the fresh key primeKeyCache
  // just seeded — otherwise a long stay on the login page would fire an
  // immediate extra /users/key call on the first tick after login.
  lastKeyRefreshAt = Date.now();

  setupActivityTracking();
  setupTokenRefreshListener();

  // Check every 1 second to show timer
  tokenTimerInterval = setInterval(async () => {
    const decoded = decodeToken();
    if (!decoded?.exp || !decoded?.iat) return;

    const nowMs = Date.now();
    const expiresAtMs = decoded.exp * 1000;
    const issuedAtMs = decoded.iat * 1000;

    const remainingMs = expiresAtMs - nowMs;
    const totalLifetimeMs = expiresAtMs - issuedAtMs;

    // Check idle status (updates wasIdle flag)
    const isIdle = isUserIdle();

    // 🔒 SAFETY: token already expired → logout
    if (remainingMs <= 0) {
      console.log("⛔ Token expired – logging out");
      forceLogout("Session expired");
      return;
    }

    // 💤 Idle auto-logout: no activity (in any tab) for 30 minutes.
    const idleMs = nowMs - lastActivity;
    if (idleMs >= IDLE_LOGOUT_MS) {
      console.log(`💤 User idle for ${Math.floor(idleMs / 60000)} min – auto-logout`);
      forceLogout("Logged out due to 30 minutes of inactivity");
      return;
    }

    // ⏳ Refresh window = last 20% of token lifetime
    const REFRESH_WINDOW_MS = totalLifetimeMs * 0.2;
    const inRefreshWindow = remainingMs <= REFRESH_WINDOW_MS;

    // Calculate percentage remaining
    const percentRemaining = Math.round((remainingMs / totalLifetimeMs) * 100);

    // Debug output only when entering refresh window (not every second)
    if (inRefreshWindow && percentRemaining > 19) {
      console.log(`⏰ Token entering refresh window (${percentRemaining}% remaining)`);
    }

    // 🔄 REFRESH CONDITIONS (like banking system):
    // - User is ACTIVE (not idle for 30+ seconds)
    // - Token is in refresh window (last 20% of lifetime)
    // - Token still has more than 5 seconds remaining
    // - No refresh currently in progress
    if (
      !isIdle &&
      inRefreshWindow &&
      remainingMs > 5000 &&
      !refreshInProgress
    ) {

      const refreshed = await refreshToken("timer");

      if (refreshed) {
      } else {
      }
    }

    // 🔑 Proactive RSA key refresh: if the user is active and the cached
    // key is over 20 min old, refetch preemptively so encryptedPost has a
    // fresh key on hand instead of leaning on the retry path. Cheap — one
    // /users/key call per 20 min per active tab. Fire-and-forget; a normal
    // on-demand fetchKey() will still run on the next encryptedPost if
    // this preemptive fetch fails.
    if (!isIdle && nowMs - lastKeyRefreshAt >= KEY_REFRESH_INTERVAL_MS) {
      lastKeyRefreshAt = nowMs;
      refreshKeyProactive().catch(() => {});
    }
  }, 1000); // Check every 1 second
};

export const stopTokenTimer = () => {
  if (tokenTimerInterval) clearInterval(tokenTimerInterval);
  cleanupTokenRefreshListener();
};
